import { Typography } from '@/components/atoms';
import { HapticTab } from '@/components/atoms/HapticTab';
import { DinarReceiptIcon, HandPropertiesIcon, HouseIcon } from '@/components/atoms/icons';
import { useIsAdhaJourney, useTranslation, useUIColors } from '@/core/hooks';
import { Tabs } from 'expo-router';
import * as React from 'react';
import { Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

function BottomTabBar({ state, descriptors, navigation }: any) {
    const isAdhaJourney = useIsAdhaJourney();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
    // Web navigation lives in WebAppLayout: left sidebar when wide, bottom bar
    // when narrow — including stack screens such as property payments.
    // Rendering it here as well doubled the bar on Home / My properties / Payment.
    if (isAdhaJourney || Platform.OS === 'web') {
        return null;
    }

    const TABS: { name: string; label: string }[] = [
        { name: 'index', label: t('home.tabLabel') },
        { name: 'properties', label: t('properties.tabLabel') },
        { name: 'payments', label: t('payments.tabLabel') },
    ];

    return (
        <View
            style={{
                backgroundColor: colors.backgroundLevel1,
                paddingBottom: Math.max(insets.bottom, 8),
                paddingTop: 8,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                flexShrink: 0,
                zIndex: 20,
                width: '100%',
                shadowColor: '#000',
                shadowOpacity: 0.15,
                shadowRadius: 16,
                shadowOffset: { width: 0, height: -4 },
            }}
        >
            {/* TabBarBackground is undefined on web/Android shim; skip JSX usage */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-around' }}>
                {state.routes.map((route: any, index: number) => {
                    const isFocused = state.index === index;
                    const onPress = () => {
                        const event = navigation.emit({
                            type: 'tabPress',
                            target: route.key,
                            canPreventDefault: true,
                        });
                        if (!isFocused && !event.defaultPrevented) {
                            navigation.navigate(route.name);
                        }
                    };

                    const label = descriptors[route.key]?.options?.title ??
                        TABS.find((t) => t.name === route.name)?.label ?? route.name;

                    const iconColor = isFocused ? colors.buttonDefault : colors.buttonDisabled;
                    const size = 24;

                    function renderIcon() {
                        switch (route.name) {
                            case 'index':
                                return <HouseIcon size={size} color={iconColor} />;
                            case 'properties':
                                return <HandPropertiesIcon size={size} color={iconColor} />;
                            case 'payments':
                                return <DinarReceiptIcon size={size} color={iconColor} />;
                            default:
                                return null;
                        }
                    }

                    return (
                        <HapticTab key={route.key} onPress={onPress} style={{ flex: 1, alignItems: 'center', paddingVertical: 8, gap: 4 }}>
                            {renderIcon()}
                            <Typography 
                                variant="caption" 
                                style={{ 
                                    fontWeight: isFocused ? '700' : '400', 
                                    color: isFocused ? colors.fontPrimary : colors.fontSecondary 
                                }}
                            >
                                {label}
                            </Typography>
                        </HapticTab>
                    );
                })}
            </View>
        </View>
    );
}

export default function TabsLayout() {
    const { t } = useTranslation();

    return (
        <Tabs
            screenOptions={{
                headerShown: false,
            }}
            tabBar={(props) => <BottomTabBar {...props} />}
        >
            <Tabs.Screen name="index" options={{ title: t('home.tabLabel') }} />
            <Tabs.Screen name="properties" options={{ title: t('properties.tabLabel') }} />
            <Tabs.Screen name="payments" options={{ title: t('payments.tabLabel') }} />
        </Tabs>
    );
}
